import React, { useMemo } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { useForm } from '.';
import Icon, { RoundIconContainer } from '../Icon';
import type { FormErrorType } from './DefaultError';

type FormItemProps<T, K> = (K extends StringKeyOf<T>
  ? {
      name: K;
      validate?: (value: T[K], values: T) => FormErrorType;
    }
  : never) & {
  tooltip?: string;
  inline?: boolean;
  required?: boolean;
  className?: string;
  label: React.ReactNode;
  labelClassName?: string;
  labelContainerClassName?: string;
  labelRightSideContent?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactElement<{
    value?: unknown;
    onChange?: Function;
    error?: FormErrorType;
  }>;
  suppressErrors?: boolean;
  hideRenderedError?: boolean;
  renderError?: null | ((error: FormErrorType) => React.ReactNode);
  errorBelowInput?: boolean;
};

export const FormItem = <
  T extends {} = any,
  K extends keyof T & string = keyof T & string,
>({
  children,
  name,
  label,
  labelRightSideContent,
  className,
  labelClassName = 'text-small',
  tooltip,
  tooltipPosition = 'bottom',
  required,
  validate,
  inline = true,
  labelContainerClassName,

  suppressErrors = false,
  hideRenderedError = false,
  errorBelowInput = false,
  renderError: ourRenderError,
  ...rest
}: FormItemProps<T, K>): React.JSX.Element => {
  const {
    getValue,
    getErrors,
    onChange,
    register,
    unregister,
    renderError: defaultRenderError,
  } = useForm<T>();

  const value = children?.props.value ?? getValue?.()[name];
  const error = getErrors?.()[name];

  const showError = useMemo(
    () => value !== undefined && value !== null,
    [value]
  );
  const renderError = ourRenderError ?? defaultRenderError;
  const renderedError =
    !!renderError && !!error && showError && renderError(error);

  const handleChange = (newValue: any | undefined) => {
    onChange?.({ ...getValue?.(), [name]: newValue } as T);
    children?.props?.onChange?.(newValue);
  };

  React.useEffect(() => {
    register?.(name, { required, validate });
    return () => {
      unregister?.(name);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, required]);

  const isErrored = !!renderedError && showError && !suppressErrors;

  return (
    <div
      className={clsx(
        className,
        'flex',
        inline
          ? 'flex-row flex-wrap gap-x-4 items-start'
          : 'flex-col gap-y-2 w-full'
      )}
    >
      {!!label && (
        <div
          className={clsx(
            twMerge(
              'text-left inline-flex items-center gap-x-1',
              inline
                ? twMerge(
                    'font-normal text-textLight min-h-[3.6rem]',
                    labelContainerClassName ||
                      'min-w-[10rem] sm:min-w-[16rem] md:min-w-[18rem] max-w-[18rem]'
                  )
                : twMerge('font-medium text-mainText', labelContainerClassName),
              labelClassName
            )
            // inline ? 'text-small' : ''
          )}
        >
          {label}
          {!!required && <span className="text-danger">*</span>}
          {!!tooltip && (
            <RoundIconContainer>
              <Icon icon="question" tooltip={tooltip} />
            </RoundIconContainer>
          )}
          {!!labelRightSideContent && (
            <span className="ml-auto">{labelRightSideContent}</span>
          )}
        </div>
      )}
      <div
        className={clsx(
          inline ? 'sm:flex-1 min-w-0' : 'w-full',
          errorBelowInput && 'flex flex-col gap-y-2'
        )}
      >
        <div
          className={clsx(
            inline
              ? clsx(
                  'flex items-center justify-start',
                  !!label && 'min-h-[3.6rem]'
                )
              : 'w-full',
            isErrored && 'outline outline-danger outline-1 rounded-sm'
          )}
        >
          {React.cloneElement(children, {
            ...rest,
            value,
            error: showError ? error : undefined,
            onChange: handleChange,
          })}
        </div>
        {!!renderedError && !hideRenderedError && (
          <div
            className={clsx(
              'text-danger text-left text-small italic mt-1',
              'w-full break-words',
              inline && !errorBelowInput && 'min-h-[3.6rem]'
            )}
          >
            {renderedError}
          </div>
        )}
      </div>
    </div>
  );
};
